'use client';

import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { useSelector } from 'react-redux';
import { selectDiscoveryMapCurrentTerritory } from 'src/redux/slices/discoveryMapSlice';
import { useGetTerritoryQuery } from 'src/api/redux';
import { convertTerritoryPolygonsToMapPaths } from 'src/utils/territory';

const isGoogleMapsLoaded = () =>
  typeof google !== 'undefined' && typeof google.maps !== 'undefined';

const DiscoveryTerritoryLayer = () => {
  const map = useMap();
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);

  const { data: territory } = useGetTerritoryQuery(
    {
      territoryId: currentTerritory?.id?.toString(),
    },
    {
      skip: !currentTerritory?.id,
    }
  );

  // Draw territory polygon (view mode only)
  useEffect(() => {
    // Clean up existing polygon if no territory selected
    if (!currentTerritory && polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
      return;
    }

    if (!map || !territory || !currentTerritory || !isGoogleMapsLoaded()) return;

    // Ensure territory data matches the selected territory (prevent stale cache data)
    if (territory.id !== currentTerritory.id) return;

    // Clean up existing polygon
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    const isNew = !!territory?.polygons;
    const territoryPolygons = isNew ? territory.polygons : territory.old_polygons;

    if (!territoryPolygons || territoryPolygons.length === 0) return;

    // Convert territory polygons to Google Maps format (supports holes)
    const polygonPaths = convertTerritoryPolygonsToMapPaths(territoryPolygons);

    const polygon = new google.maps.Polygon({
      paths: polygonPaths,
      strokeColor: '#000000',
      strokeOpacity: 100 / 255,
      strokeWeight: 1,
      fillColor: '#000000',
      fillOpacity: 35 / 255,
      editable: false,
      draggable: false,
    });

    polygon.setMap(map);
    polygonRef.current = polygon;

    return () => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, [map, territory, currentTerritory]);

  return null;
};

export default DiscoveryTerritoryLayer;
